<script setup lang="ts">
// Release Notes (RDT-211): the deployment timeline from the cockpit — when each service version
// reached each environment, grouped per env. Dev-audience internal tool, English literals (matches
// DevCockpitView). The per-version changelog (commits + linked Jira issues) is a follow-up slice.
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useConfirm } from "primevue";
import { useToast } from "primevue/usetoast";
import { CockpitAPI } from "../api";
import type { DataTableExpandedRows } from "primevue/datatable";
import type {
    DeploymentEvent,
    ReleaseNotesOverview,
    SourceData,
    VersionChangelog,
} from "../types/CockpitTypes";

// A timeline row with a stable key for DataTable dataKey + row expansion.
interface KeyedEvent extends DeploymentEvent {
    rowKey: string;
}

interface ChangelogState {
    loading: boolean;
    error: string | null;
    data: VersionChangelog | null;
}

const ENV_ORDER = ["prod", "staging", "dev"];

const source = ref<SourceData<ReleaseNotesOverview> | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);
const activeEnv = ref<string>(ENV_ORDER[0]);

const toast = useToast();
const confirm = useConfirm();
const reviewSyncing = ref(false);
const customerNotesSyncing = ref(false);
const enrichPipelineUrl = ref<string | null>(null);

const timeline = computed<DeploymentEvent[]>(() => source.value?.data.timeline ?? []);

// Memoized so row objects keep stable identity across renders (row-expansion state relies on it).
const keyedTimeline = computed<KeyedEvent[]>(() =>
    timeline.value.map((event) => ({ ...event, rowKey: `${event.env}|${event.service}|${event.version}|${event.commit ?? ""}` })),
);

// Show only environments that actually have deployment history (prod → staging → dev). Today that is
// just prod: staging/dev pin floating :staging/:dev image tags in the deployment repo, so there are no
// per-deploy version bumps to reconstruct. They return automatically once deployments are tracked as
// GitLab environments (see RDT-288).
const envs = computed<string[]>(() => {
    const present = new Set(timeline.value.map((event) => event.env));
    const ordered = ENV_ORDER.filter((env) => present.has(env));
    const extra = [...present].filter((env) => !ENV_ORDER.includes(env)).sort();
    return [...ordered, ...extra];
});

function eventsFor(env: string): KeyedEvent[] {
    return keyedTimeline.value.filter((event) => event.env === env);
}

// Expanded rows (one shared map — rowKey includes the env, so keys are unique across the tabs), and
// a changelog cache keyed by service|version (a version deployed to several envs reuses one fetch).
const expandedRows = ref<DataTableExpandedRows>({});
const changelogs = ref<Record<string, ChangelogState>>({});

function changelogKey(event: { service: string; version: string }): string {
    return `${event.service}|${event.version}`;
}

function changelogFor(event: KeyedEvent): ChangelogState | undefined {
    return changelogs.value[changelogKey(event)];
}

async function onRowExpand(event: { data: KeyedEvent }): Promise<void> {
    const key = changelogKey(event.data);
    if (changelogs.value[key]) return; // already loading, loaded, or errored
    changelogs.value[key] = { loading: true, error: null, data: null };
    try {
        const data = await CockpitAPI.getChangelog(event.data.service, event.data.version);
        changelogs.value[key] = { loading: false, error: null, data };
    } catch (e) {
        changelogs.value[key] = { loading: false, error: e instanceof Error ? e.message : String(e), data: null };
    }
}

function formatDate(iso: string | null): string {
    if (!iso) return "—";
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString();
}

function shortSha(sha: string | null): string {
    return sha ? sha.slice(0, 8) : "—";
}

function errText(e: unknown): string {
    return e instanceof Error ? e.message : String(e);
}

// RDT-287: preview (dry-run) the Confluence review-page sync, confirm, then write.
async function syncReviewPage(): Promise<void> {
    reviewSyncing.value = true;
    try {
        const preview = await CockpitAPI.syncReviewPage(true);
        const pending = preview.pending ?? [];
        // Nothing to add AND nothing to enrich → just inform.
        if (!preview.added.length && !pending.length) {
            toast.add({
                severity: "info",
                summary: "Review page",
                detail: preview.note ?? `Nothing to add — “${preview.targetTitle}” is up to date.`,
                life: 4000,
            });
            return;
        }
        // Otherwise confirm: add the ready issues and/or trigger enrichment for the ones still
        // missing a release note (they get added on a later sync once enriched).
        const lines: string[] = [];
        if (preview.added.length) {
            const verb = preview.action === "would-create" ? "Create" : "Update";
            lines.push(`${verb} “${preview.targetTitle}” and add ${preview.added.length} issue(s): ${preview.added.join(", ")}.`);
        }
        if (pending.length) {
            lines.push(`Trigger enrichment for ${pending.length} issue(s) without a release note: ${pending.join(", ")}. They’ll be added on a later sync.`);
        }
        confirm.require({
            header: "Sync review page",
            message: lines.join("\n\n"),
            icon: "pi pi-file-edit",
            acceptLabel: "Sync",
            rejectLabel: "Cancel",
            accept: runReviewSync,
        });
    } catch (e) {
        toast.add({ severity: "error", summary: "Review preview failed", detail: errText(e), life: 6000 });
    } finally {
        reviewSyncing.value = false;
    }
}

// RDT-286: preview (dry-run) which customer-relevant notes would go onto the public release-notes
// page, confirm, then publish. Re-running is how a corrected note reaches the page, so there is no
// "already ran" marker — the whole current set is republished each time.
async function syncCustomerNotes(): Promise<void> {
    customerNotesSyncing.value = true;
    try {
        const preview = await CockpitAPI.syncCustomerNotes(true);
        const notes = preview.preview ?? [];
        if (!notes.length) {
            toast.add({
                severity: "info",
                summary: "Customer release notes",
                detail: "No customer-relevant notes are ready to publish.",
                life: 4000,
            });
            return;
        }
        const features = notes.filter((note) => note.kind === "FEATURE").length;
        const months = [...new Set(notes.map((note) => note.releasedOn.slice(0, 7)))].sort();
        confirm.require({
            header: "Publish customer release notes",
            message:
                `Publish ${notes.length} note(s) — ${features} feature(s), ${notes.length - features} fix(es) — ` +
                `covering ${months[0]} to ${months[months.length - 1]}?\n\n` +
                "This replaces what the public release-notes page shows.",
            icon: "pi pi-megaphone",
            acceptLabel: "Publish",
            rejectLabel: "Cancel",
            accept: runCustomerNotesSync,
        });
    } catch (e) {
        toast.add({ severity: "error", summary: "Customer notes preview failed", detail: errText(e), life: 6000 });
    } finally {
        customerNotesSyncing.value = false;
    }
}

async function runCustomerNotesSync(): Promise<void> {
    try {
        const res = await CockpitAPI.syncCustomerNotes(false);
        toast.add({
            severity: res.note ? "warn" : "success",
            summary: "Customer release notes",
            detail: res.note ?? `Published ${res.stored ?? res.published} note(s); removed ${res.removed ?? 0} stale.`,
            life: 6000,
        });
    } catch (e) {
        toast.add({ severity: "error", summary: "Publishing failed", detail: errText(e), life: 6000 });
    }
}

async function runReviewSync(): Promise<void> {
    try {
        const res = await CockpitAPI.syncReviewPage(false);
        const parts = [`${res.action} “${res.targetTitle}” — added ${res.added.length} issue(s)`];
        const enrichFailed = !!res.pending?.length && !res.enrichmentTriggered;
        if (res.pending?.length) {
            parts.push(
                res.enrichmentTriggered
                    ? `enrichment triggered for ${res.pending.length} (added on a later sync)`
                    : `enrichment could NOT be triggered for ${res.pending.length} — check the cockpit logs`,
            );
        }
        enrichPipelineUrl.value = res.enrichmentPipelineUrl ?? null;
        toast.add({
            severity: enrichFailed ? "warn" : "success",
            summary: "Review page synced",
            detail: `${parts.join("; ")}.`,
            life: 7000,
        });
    } catch (e) {
        toast.add({ severity: "error", summary: "Review sync failed", detail: errText(e), life: 6000 });
    }
}

async function load(): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
        source.value = await CockpitAPI.getReleaseNotes();
        activeEnv.value = envs.value[0] ?? ENV_ORDER[0];
    } catch (e) {
        error.value = e instanceof Error ? e.message : String(e);
    } finally {
        loading.value = false;
    }
}

const route = useRoute();

// Deep-link support: /dev/release-notes?service=<svc>&version=<ver> (e.g. from the DevCockpit versions
// panel) selects that version's env tab and opens its changelog row.
function focusFromQuery(): void {
    const { service, version } = route?.query ?? {};
    if (typeof service !== "string" || typeof version !== "string") return;
    const match = keyedTimeline.value.find((e) => e.service === service && e.version === version);
    if (!match) return;
    activeEnv.value = match.env;
    if (!expandedRows.value[match.rowKey]) {
        expandedRows.value = { ...expandedRows.value, [match.rowKey]: true };
        void onRowExpand({ data: match });
    }
}

onMounted(async () => {
    await load();
    focusFromQuery();
});
</script>

<template>
    <div class="release-notes p-4" data-cy="release-notes">
        <div class="flex items-center gap-2 mb-4">
            <h2 class="m-0">Release Notes</h2>
            <Tag v-if="source && source.status !== 'OK'" :value="source.status" severity="warn" />
            <span v-if="source?.fetchedAt" class="text-content-muted text-sm">
                updated {{ formatDate(source.fetchedAt) }}
            </span>
            <Button
                icon="pi pi-refresh"
                size="small"
                text
                :loading="loading"
                aria-label="Refresh release notes"
                data-cy="release-notes-refresh"
                @click="load"
            />
            <span class="flex-auto" />
            <Button
                label="Sync review page"
                icon="pi pi-book"
                size="small"
                outlined
                :loading="reviewSyncing"
                aria-label="Sync Confluence review page"
                data-cy="review-sync"
                @click="syncReviewPage"
            />
            <Button
                label="Publish customer notes"
                icon="pi pi-megaphone"
                size="small"
                outlined
                :loading="customerNotesSyncing"
                aria-label="Publish customer-facing release notes"
                data-cy="customer-notes-sync"
                @click="syncCustomerNotes"
            />
            <a
                v-if="enrichPipelineUrl"
                :href="enrichPipelineUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="text-sm ml-2 self-center"
                data-cy="review-enrich-pipeline"
                >View enrichment pipeline</a
            >
        </div>

        <Message v-if="error" severity="error" :closable="false" data-cy="release-notes-error">
            {{ error }}
        </Message>
        <Message
            v-else-if="source && source.status === 'DISABLED'"
            severity="info"
            :closable="false"
            data-cy="release-notes-disabled"
        >
            Release notes are disabled — enable the cockpit release-notes source to populate the
            deployment timeline.
        </Message>
        <Message v-else-if="source?.error" severity="warn" :closable="false">
            {{ source.error }}
        </Message>

        <Tabs v-if="source && source.status !== 'DISABLED'" v-model:value="activeEnv">
            <TabList>
                <Tab v-for="env in envs" :key="env" :value="env" :data-cy="`release-notes-tab-${env}`">
                    {{ env }}
                </Tab>
            </TabList>
            <TabPanels>
                <TabPanel v-for="env in envs" :key="env" :value="env">
                    <DataTable
                        v-model:expanded-rows="expandedRows"
                        :value="eventsFor(env)"
                        data-key="rowKey"
                        size="small"
                        class="release-notes-table"
                        :data-cy="`release-notes-table-${env}`"
                        @row-expand="onRowExpand"
                    >
                        <template #empty>No deployments recorded for {{ env }}.</template>
                        <Column expander style="width: 3rem" />
                        <Column header="Deployed" style="width: 14rem">
                            <template #body="{ data }">{{ formatDate(data.deployedAt) }}</template>
                        </Column>
                        <Column header="Service" field="service" />
                        <Column header="Version">
                            <template #body="{ data }">
                                <span class="release-notes-mono">{{ data.version }}</span>
                            </template>
                        </Column>
                        <Column header="Commit" style="width: 8rem">
                            <template #body="{ data }">
                                <a
                                    v-if="data.commit && data.commitUrl"
                                    :href="data.commitUrl"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="release-notes-mono"
                                    >{{ shortSha(data.commit) }}</a
                                >
                                <span v-else class="release-notes-mono">{{ shortSha(data.commit) }}</span>
                            </template>
                        </Column>
                        <template #expansion="{ data }">
                            <div
                                class="release-notes-changelog p-2"
                                :data-cy="`release-notes-changelog-${data.service}-${data.version}`"
                            >
                                <div v-if="changelogFor(data)?.loading" class="text-content-muted">
                                    Loading changelog…
                                </div>
                                <Message
                                    v-else-if="changelogFor(data)?.error"
                                    severity="error"
                                    :closable="false"
                                >
                                    {{ changelogFor(data)?.error }}
                                </Message>
                                <template v-else-if="changelogFor(data)?.data">
                                    <div class="font-semibold mb-1">Issues</div>
                                    <ul
                                        v-if="changelogFor(data)?.data?.issues.length"
                                        class="mt-0 mb-4"
                                    >
                                        <li
                                            v-for="issue in changelogFor(data)?.data?.issues"
                                            :key="issue.key"
                                            class="mb-2"
                                        >
                                            <a :href="issue.url" target="_blank" rel="noopener noreferrer">
                                                {{ issue.key }}
                                            </a>
                                            — {{ issue.title }}
                                            <div v-if="issue.releaseNoteDe" class="text-sm mt-1">
                                                <Tag value="DE" severity="secondary" rounded />
                                                <span class="text-content-muted ml-1">
                                                    {{ issue.releaseNoteDe }}
                                                </span>
                                            </div>
                                            <div v-if="issue.releaseNoteEn" class="text-sm mt-1">
                                                <Tag value="EN" severity="info" rounded />
                                                <span class="text-content-muted ml-1">
                                                    {{ issue.releaseNoteEn }}
                                                </span>
                                            </div>
                                        </li>
                                    </ul>
                                    <div v-else class="text-content-muted mb-4">
                                        No linked RDT issues for this version.
                                    </div>

                                    <div class="font-semibold mb-1">
                                        Commits
                                        <span
                                            v-if="changelogFor(data)?.data?.commitsApproximate"
                                            class="text-content-muted text-sm"
                                        >
                                            (may include other apps in this monorepo)
                                        </span>
                                    </div>
                                    <ul
                                        v-if="changelogFor(data)?.data?.commits.length"
                                        class="mt-0 mb-1"
                                    >
                                        <li v-for="c in changelogFor(data)?.data?.commits" :key="c.sha">
                                            <a
                                                v-if="c.url"
                                                :href="c.url"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                class="release-notes-mono"
                                                >{{ c.sha.slice(0, 8) }}</a
                                            >
                                            <span v-else class="release-notes-mono">{{ c.sha.slice(0, 8) }}</span>
                                            {{ c.title }}
                                        </li>
                                    </ul>
                                    <div v-else class="text-content-muted">
                                        No commits found for this version range.
                                    </div>

                                    <div
                                        v-if="changelogFor(data)?.data?.note"
                                        class="text-content-muted text-sm mt-1"
                                    >
                                        {{ changelogFor(data)?.data?.note }}
                                    </div>
                                </template>
                            </div>
                        </template>
                    </DataTable>
                </TabPanel>
            </TabPanels>
        </Tabs>

        <Toast />
        <ConfirmDialog />
    </div>
</template>

<style scoped>
.release-notes-mono {
    font-family: var(--font-family-monospace, monospace);
}
</style>
